import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Accueil' },
    { path: '/agence', label: 'Agence' },
    { path: '/methodologie', label: 'Méthodologie' },
    { path: '/services/automatisation-ia', label: 'Automatisation IA' }
  ];

  const servicesLinks = [
    { path: '/services/creation', label: 'Création de Site' },
    { path: '/services/refonte', label: 'Refonte Web' },
    { path: '/services/ecommerce', label: 'E-commerce' },
    { path: '/services/wordpress', label: 'WordPress Expert' },
    { path: '/services/design', label: 'Design & UX' },
    { path: '/services/maintenance', label: 'Maintenance' }
  ];

  const marketingLinks = [
    { path: '/marketing/seo-local', label: 'SEO Local' },
    { path: '/marketing/google-ads', label: 'Google Ads' },
    { path: '/marketing/social-ads', label: 'Social Ads' },
    { path: '/marketing/audit', label: 'Audit & Analytics' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 w-full z-50 border-b" style={{
      background: '#0a0a0c',
      borderColor: 'rgba(255, 255, 255, 0.08)'
    }}>
      <nav className="max-w-[1400px] mx-auto px-4 lg:px-20 h-24 md:h-20 w-full grid grid-cols-5 md:flex md:items-center md:justify-between items-center gap-x-2">
        {/* Logo */}
        <Link to="/" className="col-span-2 md:col-auto flex items-center group relative w-full md:w-auto">
          <div className="absolute inset-0 bg-white blur-xl opacity-80 md:blur-2xl"></div>
          <div className="relative w-full md:w-auto bg-white rounded-xl p-1 md:p-[3px] shadow-2xl shadow-white/25 flex items-center justify-center overflow-hidden">
            <img
              src="/assets/logo.png"
              alt="WEB YOU FIRST"
              className="w-full h-auto max-h-[60px] md:h-14 md:w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="text-base font-normal transition-all duration-300 relative group"
              style={{
                color: isActive(link.path) ? '#6c5ce7' : 'rgba(255, 255, 255, 0.6)'
              }}
            >
              <span className="group-hover:text-white transition-colors duration-300">
                {link.label}
              </span>
              {isActive(link.path) && (
                <motion.div
                  layoutId={`activeTab-${link.path}`}
                  className="absolute -bottom-1 left-0 right-0 h-0.5"
                  style={{ background: '#6c5ce7' }}
                  initial={false}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              )}
            </Link>
          ))}

          {/* Services Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <button
              className="text-base font-normal transition-all duration-300 flex items-center space-x-1"
              style={{ color: location.pathname.startsWith('/services') ? '#6c5ce7' : 'rgba(255, 255, 255, 0.6)' }}
            >
              <span className="hover:text-white transition-colors duration-300">Services</span>
              <ChevronDown size={16} className={`transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {servicesOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 mt-2 w-56 rounded-xl overflow-hidden"
                  style={{
                    background: 'rgba(10, 10, 12, 0.95)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    zIndex: 100
                  }}
                >
                  {servicesLinks.map((link, index) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="block px-6 py-3 text-sm transition-all duration-200 border-b"
                      style={{
                        color: isActive(link.path) ? '#00cec9' : 'rgba(255, 255, 255, 0.7)',
                        borderColor: 'rgba(255, 255, 255, 0.05)',
                        background: isActive(link.path) ? 'rgba(0, 206, 201, 0.05)' : 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 206, 201, 0.1)';
                        e.currentTarget.style.color = '#00cec9';
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive(link.path)) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
                        }
                      }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Marketing Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setMarketingOpen(true)}
            onMouseLeave={() => setMarketingOpen(false)}
          >
            <button
              className="text-base font-normal transition-all duration-300 flex items-center space-x-1"
              style={{ color: location.pathname.startsWith('/marketing') ? '#6c5ce7' : 'rgba(255, 255, 255, 0.6)' }}
            >
              <span className="hover:text-white transition-colors duration-300">Marketing</span>
              <ChevronDown size={16} className={`transition-transform duration-300 ${marketingOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
              {marketingOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 mt-2 w-56 rounded-xl overflow-hidden"
                  style={{
                    background: 'rgba(10, 10, 12, 0.95)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    zIndex: 100
                  }}
                >
                  {marketingLinks.map((link, index) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="block px-6 py-3 text-sm transition-all duration-200 border-b"
                      style={{
                        color: isActive(link.path) ? '#00cec9' : 'rgba(255, 255, 255, 0.7)',
                        borderColor: 'rgba(255, 255, 255, 0.05)',
                        background: isActive(link.path) ? 'rgba(0, 206, 201, 0.05)' : 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(0, 206, 201, 0.1)';
                        e.currentTarget.style.color = '#00cec9';
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive(link.path)) {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = 'rgba(255, 255, 255, 0.7)';
                        }
                      }}
                    >
                      {link.label}
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link
            to="/blog"
            className="text-base font-normal transition-all duration-300 relative group"
            style={{
              color: isActive('/blog') ? '#6c5ce7' : 'rgba(255, 255, 255, 0.6)'
            }}
          >
            <span className="group-hover:text-white transition-colors duration-300">
              Blog
            </span>
            {isActive('/blog') && (
              <motion.div
                layoutId="activeTab-/blog"
                className="absolute -bottom-1 left-0 right-0 h-0.5"
                style={{ background: '#6c5ce7' }}
                initial={false}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            )}
          </Link>

          <Link
            to="/contact"
            className="text-base font-normal transition-all duration-300 relative group"
            style={{
              color: isActive('/contact') ? '#6c5ce7' : 'rgba(255, 255, 255, 0.6)'
            }}
          >
            <span className="group-hover:text-white transition-colors duration-300">
              Contact
            </span>
            {isActive('/contact') && (
              <motion.div
                layoutId="activeTab-/contact"
                className="absolute -bottom-1 left-0 right-0 h-0.5"
                style={{ background: '#6c5ce7' }}
                initial={false}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              />
            )}
          </Link>

          {/* CTA Button */}
          <Link
            to="/simulateur"
            className="px-6 py-3 text-sm font-medium text-black transition-all duration-300 hover:scale-105 hover:shadow-lg relative group overflow-hidden"
            style={{
              background: '#00cec9',
              borderRadius: '0px'
            }}
          >
            <span className="relative z-10">Devis Gratuit</span>
            <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <div className="col-span-2 col-start-4 flex justify-end items-center md:hidden h-full">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-white hover:text-[#00cec9] transition-colors duration-300 w-full flex justify-end pr-0"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={36} strokeWidth={1.5} /> : <Menu size={36} strokeWidth={1.5} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden border-t overflow-hidden"
            style={{
              background: '#0a0a0c',
              borderColor: 'rgba(255, 255, 255, 0.08)'
            }}
          >
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 text-base font-normal transition-colors duration-300"
                  style={{
                    color: isActive(link.path) ? '#6c5ce7' : 'rgba(255, 255, 255, 0.6)'
                  }}
                >
                  {link.label}
                </Link>
              ))}

              {/* Services Mobile */}
              <div className="border-t pt-3" style={{ borderColor: 'rgba(255, 255, 255, 0.08)' }}>
                <div className="text-xs font-semibold text-[#00cec9] mb-2">SERVICES</div>
                {servicesLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-2 text-sm font-normal transition-colors duration-300"
                    style={{
                      color: isActive(link.path) ? '#6c5ce7' : 'rgba(255, 255, 255, 0.6)'
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              {/* Marketing Mobile */}
              <div className="border-t pt-3" style={{ borderColor: 'rgba(255, 255, 255, 0.08)' }}>
                <div className="text-xs font-semibold text-[#00cec9] mb-2">MARKETING</div>
                {marketingLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block py-2 text-sm font-normal transition-colors duration-300"
                    style={{
                      color: isActive(link.path) ? '#6c5ce7' : 'rgba(255, 255, 255, 0.6)'
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              <Link
                to="/blog"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 text-base font-normal transition-colors duration-300 mt-2"
                style={{
                  color: isActive('/blog') ? '#6c5ce7' : 'rgba(255, 255, 255, 0.6)'
                }}
              >
                Blog
              </Link>

              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block py-2 text-base font-normal transition-colors duration-300"
                style={{
                  color: isActive('/contact') ? '#6c5ce7' : 'rgba(255, 255, 255, 0.6)'
                }}
              >
                Contact
              </Link>

              <Link
                to="/simulateur"
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full px-6 py-3 text-center text-sm font-medium text-black transition-all duration-300"
                style={{
                  background: '#00cec9',
                  borderRadius: '0px'
                }}
              >
                Devis Gratuit
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
